import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Send, MessageSquare, X, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ContactForm({ variant = 'card', onClose }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  // Pre-fill email for logged-in users
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email, name: user.name || prev.name }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: user?.name || '', email: user?.email || '', subject: '', message: '' });
      if (onClose) onClose();
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const isEmailLocked = !!user?.email;

  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder="Your name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-zinc-500"
            required
          />
          <div className="relative">
            <Input
              type="email"
              placeholder="Your email *"
              value={formData.email}
              onChange={(e) => !isEmailLocked && setFormData({ ...formData, email: e.target.value })}
              className={`bg-[#1A1A1A] border-white/10 text-white placeholder:text-zinc-500 ${isEmailLocked ? 'pr-8 cursor-not-allowed opacity-70' : ''}`}
              required
              disabled={isEmailLocked}
            />
            {isEmailLocked && <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />}
          </div>
        </div>
        <Input
          placeholder="Subject"
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-zinc-500"
        />
        <Textarea
          placeholder="Your message *"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-zinc-500 min-h-[100px]"
          required
        />
        <Button type="submit" disabled={sending} className="w-full bg-blue-600 hover:bg-blue-500">
          {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
        </Button>
      </form>
    );
  }

  return (
    <Card className="bg-[#121212] border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-400" /> Contact Support
        </CardTitle>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400">
            <X className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Name *</label>
              <Input
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#0D0D0D] border-white/10 text-white"
                required
              />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Email *</label>
              <div className="relative">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => !isEmailLocked && setFormData({ ...formData, email: e.target.value })}
                  className={`bg-[#0D0D0D] border-white/10 text-white ${isEmailLocked ? 'pr-8 cursor-not-allowed opacity-70' : ''}`}
                  required
                  disabled={isEmailLocked}
                />
                {isEmailLocked && <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />}
              </div>
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-sm mb-1 block">Subject</label>
            <Input
              placeholder="What's this about?"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="bg-[#0D0D0D] border-white/10 text-white"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-sm mb-1 block">Message *</label>
            <Textarea
              placeholder="Tell us how we can help..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="bg-[#0D0D0D] border-white/10 text-white min-h-[120px]"
              required
            />
          </div>
          <Button type="submit" disabled={sending} className="w-full bg-blue-600 hover:bg-blue-500">
            {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export function ContactButton({ className }) {
  const [showForm, setShowForm] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowForm(true)}
        className={`border-white/20 text-zinc-400 hover:text-white ${className}`}
      >
        <MessageSquare className="w-4 h-4 mr-2" /> Contact Us
      </Button>
      
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowForm(false)}>
          <div className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <ContactForm onClose={() => setShowForm(false)} />
          </div>
        </div>
      )}
    </>
  );
}

export default ContactForm;
